import Phaser from 'phaser';
import type { GameMode } from '../types';
import { audio } from '../systems/AudioManager';
import { introMusic } from '../systems/IntroMusic';
import { ProgressStore } from '../systems/ProgressStore';
import { configureLogicalCamera } from '../systems/DisplayManager';
import { LEVEL_COUNT } from '../../content/levels';

interface MenuInit {
  playIntro?: boolean;
}

export class MenuScene extends Phaser.Scene {
  private help?: Phaser.GameObjects.Container;
  private store = new ProgressStore();
  private playIntro = false;
  private sequenceComplete = true;
  private background!: Phaser.GameObjects.Image;
  private kumek!: Phaser.GameObjects.Image;
  private babel!: Phaser.GameObjects.Image;
  private menuUi!: Phaser.GameObjects.Container;
  private skipLabel?: Phaser.GameObjects.Text;
  private soundLabel?: Phaser.GameObjects.Text;
  private introTimers: Phaser.Time.TimerEvent[] = [];
  private menuButtons: Phaser.GameObjects.Text[] = [];
  private readonly frogFinalY = 265;

  constructor() {
    super('Menu');
  }

  init(data: MenuInit): void {
    this.playIntro = data.playIntro ?? false;
  }

  create(): void {
    configureLogicalCamera(this);
    const save = this.store.load();
    audio.setMuted(save.muted);
    introMusic.setMuted(save.muted);
    this.sound.mute = save.muted;
    this.cameras.main.setBackgroundColor(0x000000);
    this.sequenceComplete = !this.playIntro;
    this.introTimers = [];
    this.menuButtons = [];

    this.background = this.add.image(240, 135, 'menu-bg').setDisplaySize(480, 270).setDepth(0);
    this.kumek = this.add.image(57, this.frogFinalY, 'intro-kumek').setDisplaySize(104, 88).setOrigin(0.5, 1).setDepth(2);
    this.babel = this.add.image(158, this.frogFinalY, 'intro-babel').setDisplaySize(102, 80).setOrigin(0.5, 1).setDepth(2);

    this.menuUi = this.add.container(0, 0).setDepth(4);
    const panel = this.add.rectangle(112, 88, 216, 168, 0x06111e, 0.82).setStrokeStyle(2, 0xd5a841, 0.92);
    const title = this.add.text(18, 14, 'BĄBELKI', titleStyle(24, '#fff0c7')).setStroke('#092332', 3);
    const subtitleTitle = this.add.text(19, 43, 'SPOD ŻAB', titleStyle(18, '#8cdb7f')).setStroke('#092332', 3);
    const tagline = this.add.text(19, 67, 'Nocna przygoda w Bielsku-Białej', titleStyle(7.5, '#ffd96c'));
    const stats = this.add.text(208, 16,
      `REKORD\n${save.highScore.toString().padStart(6, '0')}\n\nKARTY ${save.postcards.length}/${LEVEL_COUNT}`,
      { ...titleStyle(7, '#d7e7da'), align: 'right', lineSpacing: 1 },
    ).setOrigin(1, 0);
    this.soundLabel = this.add.text(19, 83, `[ I ] pomoc   [ M ] dźwięk ${save.muted ? 'wył.' : 'wł.'}`, titleStyle(7.5, '#c5ddca'));

    const one = this.makeButton(112, 108, '[ 1 ]  JEDNA ŻABA', () => this.startGame('solo', 0));
    const two = this.makeButton(112, 132, '[ 2 ]  DWIE ŻABY', () => this.startGame('coop', 0));
    const continueLevel = Math.max(0, save.unlockedLevel - 1);
    const resume = this.makeButton(
      112,
      156,
      `[ K ]  KONTYNUUJ · ETAP ${continueLevel + 1}`,
      () => this.startGame('solo', continueLevel),
    );
    one.setData('key', '1');
    two.setData('key', '2');
    resume.setData('key', 'K');
    this.menuUi.add([panel, title, subtitleTitle, tagline, stats, this.soundLabel, one, two, resume]);

    if (this.playIntro) this.startIntroSequence();
    else this.showCompleteMenu();
    this.startIntroMusic();

    const keyboard = this.input.keyboard;
    keyboard?.on('keydown-ENTER', this.skipIntro, this);
    keyboard?.on('keydown-ONE', () => this.sequenceComplete && this.startGame('solo', 0));
    keyboard?.on('keydown-NUMPAD_ONE', () => this.sequenceComplete && this.startGame('solo', 0));
    keyboard?.on('keydown-TWO', () => this.sequenceComplete && this.startGame('coop', 0));
    keyboard?.on('keydown-NUMPAD_TWO', () => this.sequenceComplete && this.startGame('coop', 0));
    keyboard?.on('keydown-K', () => this.sequenceComplete && this.startGame('solo', continueLevel));
    keyboard?.on('keydown-I', this.toggleHelp, this);
    keyboard?.on('keydown-M', this.toggleMute, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      keyboard?.off('keydown-ENTER', this.skipIntro, this);
      keyboard?.off('keydown-I', this.toggleHelp, this);
      keyboard?.off('keydown-M', this.toggleMute, this);
      this.introTimers.forEach((timer) => timer.remove(false));
    });
  }

  getDebugSnapshot(): { ready: boolean; background: number; kumek: number; babel: number; menu: number; musicPlaying: boolean } {
    return {
      ready: this.sequenceComplete,
      background: this.background.alpha,
      kumek: this.kumek.alpha,
      babel: this.babel.alpha,
      menu: this.menuUi.alpha,
      musicPlaying: introMusic.isPlaying,
    };
  }

  private startIntroSequence(): void {
    this.background.setAlpha(0);
    this.kumek.setAlpha(0).setY(this.frogFinalY + 14);
    this.babel.setAlpha(0).setY(this.frogFinalY + 14);
    this.menuUi.setAlpha(0);
    this.menuButtons.forEach((button) => button.disableInteractive());
    this.skipLabel = this.add.text(469, 255, 'ENTER · POMIŃ', {
      ...titleStyle(7, '#dbe8d3'),
      backgroundColor: '#06111e99',
      padding: { x: 6, y: 4 },
    }).setOrigin(1, 0.5).setDepth(20);

    this.scheduleIntro(400, () => this.tweens.add({ targets: this.kumek, alpha: 1, y: this.frogFinalY, duration: 720, ease: 'Back.Out' }));
    this.scheduleIntro(1_400, () => this.tweens.add({ targets: this.babel, alpha: 1, y: this.frogFinalY, duration: 720, ease: 'Back.Out' }));
    this.scheduleIntro(2_450, () => this.tweens.add({ targets: this.background, alpha: 1, duration: 1_050, ease: 'Sine.InOut' }));
    this.scheduleIntro(3_650, () => this.tweens.add({
      targets: this.menuUi,
      alpha: 1,
      duration: 780,
      ease: 'Cubic.Out',
      onComplete: () => this.finishIntro(),
    }));
  }

  private scheduleIntro(delay: number, callback: () => void): void {
    this.introTimers.push(this.time.delayedCall(delay, callback));
  }

  private skipIntro(): void {
    if (this.sequenceComplete) return;
    audio.resume();
    this.startIntroMusic();
    this.finishIntro();
  }

  private finishIntro(): void {
    if (this.sequenceComplete) return;
    this.sequenceComplete = true;
    this.introTimers.forEach((timer) => timer.remove(false));
    this.introTimers = [];
    this.tweens.killTweensOf([this.background, this.kumek, this.babel, this.menuUi]);
    this.showCompleteMenu();
    this.skipLabel?.destroy();
    this.skipLabel = undefined;
  }

  private showCompleteMenu(): void {
    this.background.setAlpha(1);
    this.kumek.setAlpha(1).setY(this.frogFinalY);
    this.babel.setAlpha(1).setY(this.frogFinalY);
    this.menuUi.setAlpha(1);
    this.menuButtons.forEach((button) => button.setInteractive({ useHandCursor: true }));
  }

  private startIntroMusic(): void {
    introMusic.play();
  }

  private makeButton(x: number, y: number, label: string, action: () => void): Phaser.GameObjects.Text {
    const button = this.add.text(x, y, label, {
      ...titleStyle(8.5, '#fff0c7'),
      backgroundColor: '#102d3cdd',
      padding: { x: 8, y: 4 },
      fixedWidth: 190,
      align: 'center',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    button.on('pointerover', () => button.setStyle({ color: '#8fe17f', backgroundColor: '#1c4d4ddd' }));
    button.on('pointerout', () => button.setStyle({ color: '#f6e5b6', backgroundColor: '#102d3cdd' }));
    button.on('pointerdown', () => this.sequenceComplete && action());
    this.menuButtons.push(button);
    return button;
  }

  private startGame(mode: GameMode, levelIndex: number): void {
    audio.resume();
    this.scene.start('Postcard', { mode, levelIndex, score: 0, bonus: 0 });
  }

  private toggleMute(): void {
    const next = !audio.muted;
    audio.setMuted(next);
    introMusic.setMuted(next);
    this.sound.mute = next;
    this.store.setMuted(next);
    this.soundLabel?.setText(`[ I ] pomoc   [ M ] dźwięk ${next ? 'wył.' : 'wł.'}`);
    if (!next) this.startIntroMusic();
  }

  private toggleHelp(): void {
    if (!this.sequenceComplete) this.finishIntro();
    if (this.help) {
      this.help.destroy();
      this.help = undefined;
      return;
    }
    const container = this.add.container(240, 135).setDepth(100);
    const panel = this.add.rectangle(0, 0, 438, 226, 0x06111e, 0.97).setStrokeStyle(3, 0xd5a841);
    const title = this.add.text(0, -91, 'JAK GRAĆ', titleStyle(16, '#f2c85b')).setOrigin(0.5);
    const copy = this.add.text(-194, -61,
      'GRACZ 1   ← → · ruch   ↑ · skok   ↓ · zejście\n' +
      '           ENTER · bańka\n\n' +
      'GRACZ 2   A D · ruch   W · skok   S · zejście\n' +
      '           SPACJA · bańka\n\n' +
      'Uwięź psotnika w bańce i dotknij jej, zanim ucieknie.\n' +
      'BOSS: 3 bańki osłabiają, 4. więzi — dotknij pułapki.\n' +
      'W co-opie rozbij bańkę partnera, aby go uratować.\n\n' +
      'ESC · pauza      M · dźwięk      F · pełny ekran      I · zamknij',
      { ...titleStyle(8, '#dce9d0'), lineSpacing: 4 },
    );
    container.add([panel, title, copy]);
    this.help = container;
  }
}

function titleStyle(size: number, color: string): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: 'Verdana, "DejaVu Sans", sans-serif',
    fontSize: `${size}px`,
    color,
    fontStyle: 'bold',
    resolution: 4,
  };
}

export { titleStyle };
